import { Link } from "@inertiajs/react"
import { ArrowLeft } from "lucide-react"
import type { ReactNode } from "react"

import { RaceShield } from "@/components/race-shield"
import { NOISE_PNG } from "@/lib/noise-texture"
import { getTeamInitials } from "@/lib/race-colors"
import { leagueMatchPath } from "@/routes"

interface ReplayLayoutProps {
  children: ReactNode
  leagueId: string
  matchId: string
  homeTeamName: string
  awayTeamName: string
  homeScore: number
  awayScore: number
  extraTimePlayed?: boolean
  homeTeamRace?: string
  awayTeamRace?: string
  homeTeamHue?: number
  awayTeamHue?: number
  /** Optional turn indicator rendered on the right side of the header. */
  turnIndicator?: ReactNode
}

export default function ReplayLayout({
  children,
  leagueId,
  matchId,
  homeTeamName,
  awayTeamName,
  homeScore,
  awayScore,
  extraTimePlayed = false,
  homeTeamRace,
  awayTeamRace,
  homeTeamHue,
  awayTeamHue,
  turnIndicator,
}: ReplayLayoutProps) {
  return (
    <div className="relative flex h-screen flex-col overflow-hidden bg-[oklch(0.04_0_0)] text-[oklch(0.92_0.01_80)]">
      {/* Noise texture overlay */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: `url('${NOISE_PNG}')`,
          backgroundRepeat: "repeat",
          opacity: 0.06,
          mixBlendMode: "overlay" as const,
          zIndex: 0,
        }}
        aria-hidden="true"
      />

      {/* Thin header */}
      <header className="relative z-10 flex h-12 flex-shrink-0 items-center justify-between bg-[oklch(0.14_0.005_230)] px-4 shadow-[0_2px_8px_oklch(0_0_0/0.6)]">
        {/* Back button */}
        <Link
          href={leagueMatchPath(leagueId, matchId)}
          className="flex items-center gap-2 text-sm font-semibold transition-colors hover:text-[oklch(0.80_0.16_85)]"
        >
          <ArrowLeft className="size-4" />
          Back to Match
        </Link>

        {/* Match context: shield + team names + score + shield */}
        <div
          className="flex items-center gap-2"
          data-testid="replay-header-match"
        >
          {homeTeamRace && (
            <RaceShield
              race={homeTeamRace}
              hue={homeTeamHue}
              label={getTeamInitials(homeTeamName)}
              size="sm"
              aria-label={`${homeTeamName} shield`}
            />
          )}
          <span className="font-display text-sm leading-none font-bold tracking-wide text-[oklch(0.80_0.16_85)] uppercase">
            {homeTeamName}
          </span>
          <span className="font-display text-base leading-none font-bold tracking-wide text-[oklch(0.80_0.16_85)] uppercase">
            {homeScore} — {awayScore}
            {extraTimePlayed && (
              <span className="ml-1 text-[10px] tracking-widest opacity-80">
                AET
              </span>
            )}
          </span>
          <span className="font-display text-sm leading-none font-bold tracking-wide uppercase">
            {awayTeamName}
          </span>
          {awayTeamRace && (
            <RaceShield
              race={awayTeamRace}
              hue={awayTeamHue}
              label={getTeamInitials(awayTeamName)}
              size="sm"
              aria-label={`${awayTeamName} shield`}
            />
          )}
        </div>

        {/* Right side: turn indicator (replaces the symmetry spacer) */}
        <div className="flex w-[300px] items-center justify-end">
          {turnIndicator}
        </div>

        {/* Gold breathing border */}
        <span
          className="animate-gold-breathe pointer-events-none absolute right-0 bottom-0 left-0 h-[3px]"
          style={{
            background:
              "linear-gradient(90deg, oklch(0.35 0.06 70), oklch(0.6 0.12 85), oklch(0.8 0.16 85), oklch(0.6 0.12 85), oklch(0.35 0.06 70))",
          }}
        />
      </header>

      {/* Main content */}
      <main className="relative z-10 flex min-h-0 flex-1 overflow-hidden">
        {children}
      </main>
    </div>
  )
}
